import { useRef, useState } from "react";
import { CheckCircle2, FileDown, Lock, MessageSquareReply, Paperclip, Undo2, Upload, XCircle } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Field, InlineAlert, Input, KeyValue, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton, Textarea, Timeline, cn, notifySaveFailed, notifySaved, useSave } from "@ncp/ui";
import { OPEN_STATUSES, statusTone, useInvalidateRequests, useRequest, type MinistryRequest, type RequestAttachment } from "./api";
import { useProcedureTermWords } from "./terms";

type Action = "accept" | "clarify" | "withdraw" | "lapse" | null;

/**
 * One request as the Department reads it: what was asked, the answer with its sales lines and evidence, the files on both sides and the
 * history; and the officer's next step – accept (or mark considered), ask for a clarification, decide an extension, withdraw, or close a
 * request whose period ran out.
 */
export function RequestReviewSheet({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  // Conditions 2.13 and 2.14: the periods and provisions a request names, from their one definition and the legal rules register.
  const terms = useProcedureTermWords();
  const { locale } = useLocale();
  const request = useRequest(id);
  const r = request.data;
  const ar = locale === "ar";
  return (
    <Sheet open={id !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="end" width="max-w-4xl">
        <SheetHeader>
          <SheetTitle dir="auto">{r ? (ar && r.subjectAr ? r.subjectAr : r.subject) : t("backoffice:requests.title")}</SheetTitle>
          <SheetDescription>{r ? `${r.reference} · ${t(`backoffice:requests.kindLong.${r.kind}`, terms)}` : ""}</SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-6">
          {request.isLoading || !r ? <Skeleton className="h-96" /> : <ReviewBody r={r} />}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function ReviewBody({ r }: { r: MinistryRequest }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const open = OPEN_STATUSES.includes(r.status);
  const statusLabel = r.status === "Completed" ? t(`backoffice:requests.completed.${r.kind}`) : t(`backoffice:requests.status.${r.status}`);
  const answered = r.status === "Submitted" || r.status === "Completed" || (r.round > 0 && r.answers.length > 0);
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Badge dot variant={statusTone(r.status, r.overdue)}>{statusLabel}</Badge>
        {open && r.overdue ? <Badge variant="danger" size="sm">{t("backoffice:requests.overdue")}</Badge> : null}
        {r.status === "Completed" && r.salesRowsFiled > 0 ? <Badge variant="success" size="sm">{t("backoffice:requests.review.salesFiled", { count: r.salesRowsFiled })}</Badge> : null}
        {r.testFile ? <Badge variant="camel" size="sm" data-testid="request-test-file">{t("backoffice:requests.review.testFile")}</Badge> : null}
      </div>

      <KeyValue columns={3} items={[
        { label: t("backoffice:requests.review.contact"), value: <span dir="auto">{ar ? r.recipientNameAr : r.recipientName}{r.contactName || r.contactEmail ? <span className="block text-xs text-muted-foreground">{[r.contactName, r.contactEmail].filter(Boolean).join(" · ")}</span> : null}</span> },
        { label: t("backoffice:requests.columns.due"), value: <span className={cn(open && r.overdue && "text-danger-700")}>{formatDate(r.dueOn, locale)}</span> },
        { label: t("backoffice:requests.columns.about"), value: r.caseNumber ? <span className="font-mono">{r.caseNumber}</span> : <span dir="auto">{ar && r.studyTitleAr ? r.studyTitleAr : r.studyTitle}</span> },
      ]} />

      <ActionBar r={r} />

      <section>
        <h4 className="mb-2 text-sm font-semibold">{t("backoffice:requests.review.answer")}</h4>
        {!answered ? <p className="text-sm text-muted-foreground">{t("backoffice:requests.review.noAnswer")}</p> : <AnswerView r={r} />}
      </section>

      {/* Each earlier round stays as it was submitted (S 2.7): an answer to a clarification is read beside the round it follows. */}
      {r.earlierRounds?.length ? (
        <section data-testid="request-earlier-rounds">
          <h4 className="mb-2 text-sm font-semibold">{t("backoffice:requests.review.earlierRounds")}</h4>
          <div className="space-y-2">
            {[...r.earlierRounds].reverse().map((round) => (
              <details key={round.round} className="rounded-lg border border-sand-200 p-3">
                <summary className="cursor-pointer text-sm font-medium">{t("backoffice:requests.review.round", { n: round.round, date: formatDateTime(round.submittedAt, locale) })}</summary>
                <div className="mt-3"><AnswerView r={{ ...r, ...round, attachments: [], submittedAt: round.submittedAt }} /></div>
              </details>
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <h4 className="mb-2 text-sm font-semibold">{t("backoffice:requests.review.request")}</h4>
        <div className="space-y-3 rounded-lg border border-sand-200 p-4 text-sm">
          <p className="whitespace-pre-wrap" dir="auto">{ar && r.summaryAr ? r.summaryAr : r.summary}</p>
          {r.context ? <p className="whitespace-pre-wrap text-aeblack-900/85" dir="auto">{ar && r.contextAr ? r.contextAr : r.context}</p> : null}
          <ol className="list-decimal space-y-1 ps-5">
            {r.items.map((i) => <li key={i.key} dir="auto">{ar && i.labelAr ? i.labelAr : i.label} <span className="text-[11px] text-muted-foreground">· {t(`backoffice:requests.itemKinds.${i.kind}`)}{i.years.length ? ` ${i.years.join(", ")}` : ""}</span></li>)}
          </ol>
          <p className="text-[11px] text-muted-foreground">{ar ? r.legalBasisAr : r.legalBasis} · {t("backoffice:requests.review.issuedBy", { name: r.issuedByName })} · {formatDate(r.sentAt, locale)}</p>
        </div>
      </section>

      <section>
        <h4 className="mb-2 text-sm font-semibold">{t("backoffice:requests.review.files")}</h4>
        <Files r={r} />
      </section>

      <section>
        <h4 className="mb-2 text-sm font-semibold">{t("backoffice:requests.review.history")}</h4>
        <Timeline items={[...r.timeline].reverse().map((e, i) => ({
          id: `${e.at}-${i}`, title: t(`backoffice:requests.events.${e.kind}`, { defaultValue: e.kind }), time: formatDateTime(e.at, locale), actor: e.actorName,
          detail: e.note ? <span dir="auto">{e.note}</span> : undefined, kind: e.side === "Ministry" ? "milestone" : e.side === "System" ? "system" : "human",
        }))} />
      </section>
    </>
  );
}

function AnswerView({ r }: { r: MinistryRequest }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const fmt = (v: number) => new Intl.NumberFormat(ar ? "ar-AE" : "en-AE", { maximumFractionDigits: 0 }).format(v);
  return (
    <div className="space-y-4 text-sm">
      {r.position ? (
        <InlineAlert tone={r.position === "SeriousConcerns" ? "danger" : r.position === "ConcernsAddressable" ? "warning" : "info"} title={`${t("backoffice:requests.review.position")}: ${t(`backoffice:requests.review.positions.${r.position}`)}`} />
      ) : null}
      {r.statement ? <Block title={t(`backoffice:requests.review.statement.${r.kind}`)} text={r.statement} /> : null}
      {r.marketAssessment ? <Block title={t("backoffice:requests.review.marketAssessment")} text={r.marketAssessment} /> : null}
      {r.proposedConditions ? <Block title={t("backoffice:requests.review.conditions")} text={r.proposedConditions} /> : null}
      <ol className="space-y-3">
        {r.items.map((item, n) => {
          const a = r.answers.find((x) => x.key === item.key);
          const files = r.attachments.filter((f) => f.side === "Recipient" && f.itemKey === item.key);
          if (!a && files.length === 0) return null;
          return (
            <li key={item.key} className="rounded-lg border border-sand-200 p-3">
              <p className="text-xs font-semibold text-aeblack-900" dir="auto">{n + 1}. {ar && item.labelAr ? item.labelAr : item.label} {a?.confidential ? <Badge variant="outline" size="sm" className="ms-1"><Lock /> {t("backoffice:requests.review.confidential")}</Badge> : null}</p>
              {a?.text ? <p className="mt-1 whitespace-pre-wrap" dir="auto">{a.text}</p> : null}
              {a?.number != null ? <p className="mt-1 tabular-nums">{a.number} {item.unit}</p> : null}
              {a?.rows.length ? (
                <table className="mt-2 w-full text-xs">
                  <thead className="text-xs text-sand-700"><tr>{(["productLine", "hsCode", "year", "flow", "value", "volume"] as const).map((h) => <th key={h} className={cn("py-1 font-medium", h === "value" || h === "volume" ? "text-end" : "text-start")}>{t(`backoffice:requests.sales.${h}`)}</th>)}</tr></thead>
                  <tbody className="divide-y divide-sand-100">
                    {a.rows.map((row, i) => (
                      <tr key={i}>
                        <td className="py-1" dir="auto">{row.productLine}</td><td className="font-mono">{row.hsCode}</td><td>{row.year}</td><td>{t(`backoffice:requests.sales.flows.${row.flow}`)}</td>
                        <td className="text-end tabular-nums">{row.valueAed != null ? fmt(row.valueAed) : ""}</td><td className="text-end tabular-nums">{row.volume != null ? `${fmt(row.volume)} ${row.volumeUnit ?? ""}` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              {files.length > 0 ? <FileList requestId={r.id} files={files} /> : null}
            </li>
          );
        })}
      </ol>
      {r.sources ? <Block title={t("backoffice:requests.review.sources")} text={r.sources} /> : null}
      {r.confidentialityNote ? <Block title={t("backoffice:requests.review.confidentialityNote")} text={r.confidentialityNote} /> : null}
      {r.submittedByName ? <p className="text-xs text-muted-foreground">{t("backoffice:requests.review.respondent", { name: [r.submittedByName, r.submittedByTitle].filter(Boolean).join(", ") })}{r.submittedAt ? ` · ${formatDateTime(r.submittedAt, locale)}` : ""}</p> : null}
    </div>
  );
}

function Block({ title, text }: { title: string; text: string }) {
  return <div><p className="text-xs font-semibold text-aeblack-900">{title}</p><p className="mt-1 whitespace-pre-wrap" dir="auto">{text}</p></div>;
}

function Files({ r }: { r: MinistryRequest }) {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateRequests();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const general = r.attachments.filter((a) => a.side === "Ministry" || !a.itemKey);
  const upload = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const answer = await api.upload(`/api/requests/${r.id}/attachments`, fd);
      notifySaved(answer, { title: t("backoffice:requests.review.attached"), description: file.name });
      invalidate();
    } catch (e) { notifySaveFailed(e); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <FileList requestId={r.id} files={general} labelled />
      {OPEN_STATUSES.includes(r.status) && !r.submittedAt ? (
        <>
          <input ref={input} type="file" className="hidden" accept=".pdf,.docx,.xlsx,.pptx,.csv,.txt,.png,.jpg,.jpeg" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ""; }} />
          <Button size="sm" variant="outline" loading={busy} onClick={() => input.current?.click()}>{busy ? null : <Upload />} {t("backoffice:requests.review.attach")}</Button>
        </>
      ) : null}
    </div>
  );
}

function FileList({ requestId, files, labelled }: { requestId: string; files: RequestAttachment[]; labelled?: boolean }) {
  const { t } = useTranslation();
  const api = useApi();
  if (files.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {files.map((f) => (
        <li key={f.id} className="flex items-center gap-2 rounded-md border border-sand-200 px-2.5 py-1.5 text-xs">
          <Paperclip className="size-3.5 shrink-0 text-sand-600" />
          <button type="button" className="min-w-0 flex-1 truncate text-start hover:underline" onClick={() => void api.download(`/api/requests/${requestId}/attachments/${f.id}`, f.fileName)}>{f.fileName}</button>
          {labelled ? <span className="text-muted-foreground">{f.side === "Ministry" ? t("backoffice:requests.review.ministryFile") : t("backoffice:requests.review.recipientFile")}</span> : null}
          {f.confidential ? <Badge variant="outline" size="sm"><Lock /> {t("backoffice:requests.review.confidential")}</Badge> : null}
          <FileDown className="size-3.5 text-sand-500" />
        </li>
      ))}
    </ul>
  );
}

function ActionBar({ r }: { r: MinistryRequest }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const invalidate = useInvalidateRequests();
  const [action, setAction] = useState<Action>(null);
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const open = OPEN_STATUSES.includes(r.status);
  const done = () => { setAction(null); setNote(""); setDue(""); invalidate(); };
  // Each action answers the request as stored, with the officer's note on it.
  const run = useSave({
    expect: "record",
    mutationFn: async (a: Exclude<Action, null>) => {
      switch (a) {
        case "accept": return api.post<{ salesRowsFiled: number }>(`/api/requests/${r.id}/complete`, { note: note || null });
        case "clarify": return api.post(`/api/requests/${r.id}/clarify`, { note, dueOn: due || null });
        case "withdraw": return api.post(`/api/requests/${r.id}/withdraw`, { reason: note });
        case "lapse": return api.post(`/api/requests/${r.id}/lapse`, { note: note || null });
      }
    },
    saved: (res, a) => (a === "accept" ? t(`backoffice:requests.review.accepted.${r.kind}`, { count: (res as { salesRowsFiled?: number })?.salesRowsFiled ?? 0 }) : a === "clarify" ? t("backoffice:requests.review.clarified") : a === "withdraw" ? t("backoffice:requests.review.withdrawn") : t("backoffice:requests.review.lapsed")),
    onSuccess: done,
  });
  const extension = useSave({
    mutationFn: (grant: boolean) => api.post(`/api/requests/${r.id}/extension`, { grant, dueOn: null, note: null }),
    expect: "record",
    saved: (_, grant) => (grant ? t("backoffice:requests.review.granted") : t("backoffice:requests.review.refused")),
    onSuccess: done,
  });

  if (!open && r.status !== "Submitted") return null;
  const label = { accept: t(`backoffice:requests.review.accept.${r.kind}`), clarify: t("backoffice:requests.review.clarify"), withdraw: t("backoffice:requests.review.withdraw"), lapse: t("backoffice:requests.review.lapse") };
  const noteLabel = { accept: t("backoffice:requests.review.acceptNote"), clarify: t("backoffice:requests.review.clarifyNote"), withdraw: t("backoffice:requests.review.withdrawReason"), lapse: t("backoffice:requests.review.lapseNote") };
  // Condition 1.9: figures the Data Bank's other sources do not bear out are accepted only with the officer's reasons.
  const discrepancies = r.crossCheck ?? [];
  const reasonsNeeded = action === "accept" && discrepancies.length > 0;
  const noteRequired = action === "clarify" || action === "withdraw" || reasonsNeeded;
  const minNote = action === "clarify" ? 10 : reasonsNeeded ? 20 : 5;
  return (
    <div className="space-y-3 rounded-lg border border-sand-200 bg-sand-50/60 p-3">
      {r.status === "Submitted" && discrepancies.length > 0 ? (
        <InlineAlert tone="warning" title={t("backoffice:requests.review.crossCheckTitle", { count: discrepancies.length })} data-testid="request-cross-check">
          <span className="block">{t("backoffice:requests.review.crossCheckHint")}</span>
          <ul className="mt-1 list-disc space-y-0.5 ps-5">{discrepancies.map((d, i) => <li key={i} dir="auto">{locale === "ar" ? d.messageAr : d.message}</li>)}</ul>
        </InlineAlert>
      ) : null}
      {r.extensionRequestedTo && open ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="flex-1" dir="auto">{t("backoffice:requests.review.extension", { name: locale === "ar" ? r.recipientNameAr : r.recipientName, date: formatDate(r.extensionRequestedTo, locale) })}{r.extensionReason ? <span className="block text-xs text-muted-foreground">{r.extensionReason}</span> : null}</span>
          <Button size="sm" variant="outline" loading={extension.isPending && extension.variables === true} onClick={() => extension.mutate(true)}>{t("backoffice:requests.review.grant")}</Button>
          <Button size="sm" variant="ghost" loading={extension.isPending && extension.variables === false} onClick={() => extension.mutate(false)}>{t("backoffice:requests.review.refuse")}</Button>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {r.status === "Submitted" ? (
          <>
            <Button size="sm" variant="gold" primary onClick={() => setAction("accept")}><CheckCircle2 /> {label.accept}</Button>
            <Button size="sm" variant="outline" onClick={() => setAction("clarify")}><MessageSquareReply /> {label.clarify}</Button>
          </>
        ) : null}
        {open ? <Button size="sm" variant="ghost" onClick={() => setAction("withdraw")}><Undo2 /> {label.withdraw}</Button> : null}
        {open && r.overdue ? <Button size="sm" variant="ghost" onClick={() => setAction("lapse")}><XCircle /> {label.lapse}</Button> : null}
      </div>
      {action ? (
        <div className="space-y-2 border-t border-sand-200 pt-3">
          <Field label={reasonsNeeded ? t("backoffice:requests.review.crossCheckReasons") : noteLabel[action]} required={noteRequired}>{(fid) => <Textarea id={fid} rows={3} value={note} onChange={(e) => setNote(e.target.value)} draft={`request:${r.id}:${action}:note`} dir="auto" />}</Field>
          {action === "clarify" ? <Field label={t("backoffice:requests.review.clarifyDue")}>{(fid) => <Input id={fid} type="date" value={due} onChange={(e) => setDue(e.target.value)} className="max-w-48" />}</Field> : null}
          <div className="flex gap-2">
            <Button size="sm" variant={action === "accept" ? "gold" : "outline"} disabled={noteRequired && note.trim().length < minNote} loading={run.isPending} onClick={() => run.mutate(action)}>{label[action]}</Button>
            <Button size="sm" variant="ghost" onClick={() => setAction(null)}>{t("actions.cancel")}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
